import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Gift, MapPin, Phone, Clock, Star, ChevronRight, Store, Utensils } from 'lucide-react';
import { Button } from '@/components/ui/button';
import GuestChat from '@/components/chat/GuestChat';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PublicStorefrontPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('gift-cards');

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/store/${slug}`);
        if (!res.ok) throw new Error('Store not found');
        setData(await res.json());
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  if (loading) return <LoadingScreen />;
  if (error || !data) return <NotFoundScreen />;

  const { restaurant, campaigns, menu, store_config } = data;
  const accent = store_config?.accent_color || '#d4a053';

  return (
    <div className="min-h-screen bg-[#0a0a0f]" data-testid="public-storefront">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        {restaurant.cover_image_url ? (
          <img src={restaurant.cover_image_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-[#1a1a2e] to-[#0a0a0f]" />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0a0a0f]/60 to-[#0a0a0f]" />

        <div className="relative max-w-5xl mx-auto px-4 pt-12 pb-16">
          <div className="flex items-center gap-5 mb-6">
            {restaurant.image_url ? (
              <img src={restaurant.image_url} alt={restaurant.name}
                className="w-20 h-20 rounded-2xl object-cover border-2 shadow-xl"
                style={{ borderColor: accent }} />
            ) : (
              <div className="w-20 h-20 rounded-2xl flex items-center justify-center text-white"
                style={{ background: accent }}>
                <Store className="w-9 h-9" />
              </div>
            )}
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-white tracking-tight" data-testid="store-name">
                {restaurant.name}
              </h1>
              {restaurant.cuisine_type && (
                <p className="text-sm text-gray-400 mt-1">{restaurant.cuisine_type}</p>
              )}
              <div className="flex items-center gap-4 mt-2 text-sm text-gray-400">
                {restaurant.rating > 0 && (
                  <span className="flex items-center gap-1">
                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    {restaurant.rating} ({restaurant.total_reviews})
                  </span>
                )}
                {restaurant.address && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5" /> {restaurant.address}
                  </span>
                )}
              </div>
            </div>
          </div>

          {restaurant.description && (
            <p className="text-gray-300 max-w-2xl text-sm leading-relaxed">{restaurant.description}</p>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-5xl mx-auto px-4">
        <div className="flex gap-1 border-b border-white/10 mb-8">
          {[
            { id: 'gift-cards', label: 'Gift Cards', icon: Gift },
            ...(menu?.length ? [{ id: 'menu', label: 'Menu', icon: Utensils }] : []),
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'text-white border-current'
                  : 'text-gray-500 border-transparent hover:text-gray-300'
              }`}
              style={activeTab === tab.id ? { color: accent } : {}}
              data-testid={`tab-${tab.id}`}>
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Gift Cards Tab */}
        {activeTab === 'gift-cards' && (
          <div className="pb-16">
            {campaigns.length === 0 ? (
              <div className="text-center py-20 text-gray-500">
                <Gift className="w-14 h-14 mx-auto mb-4 opacity-40" />
                <p className="text-lg">No gift card campaigns available right now.</p>
                <p className="text-sm mt-1">Check back soon!</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {campaigns.map(c => (
                  <CampaignCard key={c.id} campaign={c} slug={slug} accent={accent} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Menu Tab */}
        {activeTab === 'menu' && (
          <div className="pb-16 space-y-8">
            {menu.map(cat => (
              <div key={cat.id}>
                <h3 className="text-lg font-semibold text-white mb-3">{cat.name}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {cat.items.map(item => (
                    <div key={item.id}
                      className="flex gap-3 p-3 rounded-xl bg-white/5 border border-white/5 hover:border-white/10 transition">
                      {item.image_url && (
                        <img src={item.image_url} alt={item.name}
                          className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-start gap-2">
                          <h4 className="text-sm font-medium text-white truncate">{item.name}</h4>
                          <span className="text-sm font-semibold text-white whitespace-nowrap">${Number(item.price).toFixed(2)}</span>
                        </div>
                        {item.description && (
                          <p className="text-xs text-gray-500 line-clamp-2 mt-0.5">{item.description}</p>
                        )}
                        <div className="flex gap-1 mt-1">
                          {item.is_popular && <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-400">Popular</span>}
                          {item.is_spicy && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-400">Spicy</span>}
                          {item.is_vegetarian && <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-500/20 text-green-400">Veg</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="border-t border-white/5 py-8 text-center text-xs text-gray-600">
        <div className="max-w-5xl mx-auto px-4 flex flex-col sm:flex-row justify-between items-center gap-2">
          <span>Powered by <span className="text-gray-400 font-medium">Chain29</span></span>
          <div className="flex gap-4">
            {restaurant.phone && (
              <a href={`tel:${restaurant.phone}`} className="flex items-center gap-1 text-gray-500 hover:text-gray-300">
                <Phone className="w-3 h-3" /> {restaurant.phone}
              </a>
            )}
          </div>
        </div>
      </footer>
      <GuestChat restaurantId={restaurant.id} restaurantName={restaurant.name} accent={accent} sourcePage="store" />
    </div>
  );
}

function CampaignCard({ campaign, slug, accent }) {
  const remaining = campaign.remaining;
  const isSoldOut = remaining <= 0;

  return (
    <Link to={`/store/${slug}/${campaign.slug}`}
      className="group block rounded-2xl overflow-hidden border border-white/8 bg-white/[0.03] hover:bg-white/[0.06] transition-all hover:border-white/15"
      data-testid={`campaign-card-${campaign.id}`}>
      {campaign.banner_image_url && (
        <img src={campaign.banner_image_url} alt={campaign.name}
          className="w-full h-40 object-cover" />
      )}
      <div className="p-5">
        <div className="flex justify-between items-start mb-2">
          <div>
            <h3 className="text-lg font-semibold text-white group-hover:text-opacity-100">{campaign.name}</h3>
            <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${
              campaign.campaign_type === 'free'
                ? 'bg-emerald-500/15 text-emerald-400'
                : 'bg-blue-500/15 text-blue-400'
            }`}>
              {campaign.campaign_type === 'free' ? 'FREE' : 'Purchase'}
            </span>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-white">${Number(campaign.gift_card_amount).toFixed(0)}</div>
            <div className="text-xs text-gray-500">gift card</div>
          </div>
        </div>

        {campaign.description && (
          <p className="text-sm text-gray-400 line-clamp-2 mt-2">{campaign.description}</p>
        )}

        <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/5">
          {isSoldOut ? (
            <span className="text-xs font-medium text-red-400">Sold Out</span>
          ) : (
            <span className="text-xs text-gray-500">{remaining} remaining</span>
          )}
          <span className="flex items-center gap-1 text-sm font-medium group-hover:gap-2 transition-all"
            style={{ color: accent }}>
            {isSoldOut ? 'View' : (campaign.campaign_type === 'free' ? 'Claim Now' : 'Buy Now')}
            <ChevronRight className="w-4 h-4" />
          </span>
        </div>
      </div>
    </Link>
  );
}

function LoadingScreen() {
  return (
    <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
      <img src="/chain29-logo.png" alt="Loading" className="w-12 h-12 rounded-xl object-cover animate-pulse" />
    </div>
  );
}

function NotFoundScreen() {
  return (
    <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center text-center px-4">
      <div>
        <Store className="w-16 h-16 text-gray-700 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-white mb-2">Store Not Found</h2>
        <p className="text-gray-500 text-sm">This storefront doesn't exist or has been removed.</p>
      </div>
    </div>
  );
}
